import React, { useEffect } from "react";
import * as Realm from "realm-web";
import PropTypes from "prop-types";
import {
  hasLoggedInUser,
  loginAnonymous,
  logoutCurrentUser,
  getCurrentUser,
} from "./stitch/authentication";

import { app } from "./stitch";

// Create a React Context that lets us expose and access auth state
// without passing props through many levels of the component tree

export const StitchAuthContext = React.createContext();

// Create a React Hook that lets us get data from our auth context
export function useStitchAuth() {
  const context = React.useContext(StitchAuthContext);
  if (!context) {
    throw new Error(`useStitchAuth must be used within a StitchAuthProvider`);
  }

  // context = value prop of the nearest <StitchAuthContext Provider>
  // allows us to not use consumer
  return context;
}

// Create a component that controls auth state and exposes it via
// the React Context we created.
export function StitchAuthProvider(props) {
  const [authState, setAuthState] = React.useState({
    isLoggedIn: hasLoggedInUser(),
    currentUser: getCurrentUser(),
  });

  useEffect(() => {
    if (!authState.isLoggedIn) {
      handleAnonymousLogin();
    }
  }, []);

  useEffect(() => {
    console.log({ authState });
    console.log("maybe oauth redirect & set isLogged in state");
  }, [authState]);

  const [loginErrors, setLoginErrors] = React.useState({});

  // Authentication Actions
  const handleLogin = async (provider) => {
    if (!authState.isLoggedIn) {
      switch (provider) {
        case "anonymous":
          return loginAnonymous();
        case "google":
          return handleGoogleLogin();
        default: {
        }
      }
    }
  };
  const handleAnonymousLogin = () => {
    return app
      .logIn(Realm.Credentials.anonymous())
      .then((authedUser) => {
        setAuthState({
          ...authState,
          isLoggedIn: false,
          currentUser: app.currentUser,
        });
      })
      .catch((err) => {
        setLoginErrors(err);
      });
  };

  const handleGoogleLogin = () => {
    const redirectUri = "http://localhost:3000/";
    const credential = Realm.Credentials.google(redirectUri);

    return app
      .logIn(credential)
      .then((authedUser) => {
        setAuthState({
          ...authState,
          isLoggedIn: true,
          currentUser: app.currentUser,
        });
        Realm.handleAuthRedirect();
      })
      .catch((err) => {
        setLoginErrors(err);
      });
  };
  const handleEmailLogin = (loginInfo) => {
    const credential = Realm.Credentials.emailPassword(
      loginInfo.email,
      loginInfo.password
    );

    console.log("called???");

    return app
      .logIn(credential)
      .then((authedUser) => {
        setAuthState({
          ...authState,
          isLoggedIn: true,
          currentUser: app.currentUser,
        });
        console.log("called??? 2");
      })
      .catch((err) => {
        setLoginErrors(err);
      });
  };

  const handleLogout = async () => {
    const { isLoggedIn } = authState;
    if (isLoggedIn) {
      await logoutCurrentUser();
      setAuthState({
        ...authState,
        isLoggedIn: false,
        currentUser: null,
      });

      handleAnonymousLogin();
    } else {
      console.log(`can't handleLogout when no user is logged in`);
    }
  };

  const handleEmailRegister = async (registerInfo) => {
    // emailpasswordClient returns a Promise, and it this promise has no value if it succeeds
    // however, since then statement triggers if there is no error, going into then statement means success
    // in react, it's perfect to access a promise, use then to get its promiseValue then set state accordingly
    // which means, we return a promise from handleEmailRegister as opposed to direct value (it's hard to return direct value)

    var getResult = function () {
      return app.emailPasswordAuth
        .registerUser(registerInfo.email, registerInfo.password1)
        .then((result) => {
          return new Promise(function (resolve, reject) {
            resolve("success");
          });
        })
        .catch((err) => {
          return err;
        });
    };
    return await getResult();
  };

  const sendResetPasswordEmail = async (email) => {
    var getResult = function () {
      return app.emailPasswordAuth
        .sendResetPasswordEmail(email)
        .then(() => {
          console.log("here");
          return new Promise(function (resolve, reject) {
            resolve("success");
          });
        })
        .catch((err) => {
          console.log("error", err);
          return err;
        });
    };

    return await getResult();
  };

  // We useMemo to improve performance by eliminating some re-renders
  const authInfo = React.useMemo(() => {
    const { isLoggedIn, currentUser } = authState;
    const value = {
      isLoggedIn,
      currentUser,
      loginErrors,
      actions: {
        handleLogin,
        handleLogout,
        handleEmailLogin,
        handleEmailRegister,
        sendResetPasswordEmail,
      },
    };
    return value;
  }, [authState.isLoggedIn, loginErrors, authState.currentUser]);
  return (
    <StitchAuthContext.Provider value={authInfo}>
      {props.children}
    </StitchAuthContext.Provider>
  );
}

StitchAuthProvider.propTypes = {
  children: PropTypes.element,
};
